import {
  E_HoldingsImportAnalyticsEvent,
  E_OnboardingAnalyticsEvent,
} from "@lattice/shared-frontend/onboarding";

/**
 * Mixpanel event name constants.
 * Using a const object prevents typos and enables autocomplete.
 *
 * Keep shared event names identical to mobile for cross-platform funnel consistency.
 */
export const MixpanelEvents = {
  // App lifecycle
  APP_LAUNCHED: "App Launched",
  SCREEN_VIEWED: "Screen Viewed",
  PAGE_VIEWED: "Page Viewed",

  // Shell (chat-first web frame; taxonomy shared with mobile)
  SHELL_DRAWER_OPENED: "Shell Drawer Opened",
  SHELL_THREAD_SELECTED: "Shell Thread Selected",
  SHELL_NEW_CHAT_STARTED: "Shell New Chat Started",
  SHELL_SETTINGS_OPENED: "Shell Settings Opened",
  SHELL_PREMADE_QUESTIONS_SHOWN: "Shell Premade Questions Shown",
  SHELL_PREMADE_QUESTION_TAPPED: "Shell Premade Question Tapped",

  // Auth flow
  SIGN_UP_TAPPED: "Sign Up Tapped",
  SIGN_IN_TAPPED: "Sign In Tapped",
  SIGN_UP_COMPLETED: "Sign Up Completed",
  INTRO_SLIDE_VIEWED: "Intro Slide Viewed",
  INTRO_GET_STARTED_TAPPED: "Intro Get Started Tapped",

  // Onboarding
  ...E_OnboardingAnalyticsEvent,

  // Holdings import
  ...E_HoldingsImportAnalyticsEvent,

  // Chat / Prism
  CHAT_MESSAGE_SENT: "Chat Message Sent",
  CHAT_THREAD_CREATED: "Chat Thread Created",
  CHAT_THREAD_DELETED: "Chat Thread Deleted",
  CHAT_THREAD_SELECTED: "Chat Thread Selected",
  CHAT_THREAD_SEARCHED: "Chat Thread Searched",
  CHAT_FOLLOW_UP_SHOWN: "Chat Follow Up Shown",
  CHAT_FOLLOW_UP_TAPPED: "Chat Follow Up Tapped",
  CHAT_SUGGESTED_PROMPT_TAPPED: "Chat Suggested Prompt Tapped",
  CHAT_STREAMING_STOPPED: "Chat Streaming Stopped",
  CHAT_MESSAGE_RETRIED: "Chat Message Retried",
  CHAT_ERROR_DISMISSED: "Chat Error Dismissed",
  CHAT_MESSAGE_LIKED: "Chat Message Liked",
  CHAT_MESSAGE_DISLIKED: "Chat Message Disliked",
  CHAT_FEEDBACK_UNDONE: "Chat Feedback Undone",
  CHAT_MUTATION_ACCEPTED: "Chat Mutation Accepted",
  CHAT_MUTATION_REJECTED: "Chat Mutation Rejected",
  CHAT_MESSAGE_COPIED: "Chat Message Copied",
  CHAT_LINK_TAPPED: "Chat Link Tapped",
  CHAT_LIMIT_BANNER_SHOWN: "Chat Limit Banner Shown",
  CHAT_LIMIT_BANNER_DISMISSED: "Chat Limit Banner Dismissed",
  CHAT_LIMIT_SUBSCRIBE_TAPPED: "Chat Limit Subscribe Tapped",
  CHAT_LIMIT_UPGRADE_MODAL_SHOWN: "Chat Limit Upgrade Modal Shown",
  CHAT_LIMIT_UPGRADE_REQUESTED: "Chat Limit Upgrade Requested",
  CHAT_LIMIT_EXPLORE_PLANS_TAPPED: "Chat Limit Explore Plans Tapped",
  CHAT_DICTATION_TAPPED: "Chat Dictation Tapped",
  CHAT_DICTATION_STARTED: "Chat Dictation Started",
  CHAT_DICTATION_PERMISSION_DENIED: "Chat Dictation Permission Denied",
  CHAT_DICTATION_CANCELLED: "Chat Dictation Cancelled",
  CHAT_DICTATION_SUBMITTED: "Chat Dictation Submitted",
  CHAT_DICTATION_TRANSCRIBED: "Chat Dictation Transcribed",
  CHAT_DICTATION_FAILED: "Chat Dictation Failed",

  // Stocks
  STOCK_VIEWED: "Stock Viewed",
  MARKET_TAB_CHANGED: "Market Tab Changed",

  // Search
  SEARCH_PERFORMED: "Search Performed",
  SEARCH_RESULT_SELECTED: "Search Result Selected",

  // Subscription / Paywall
  PAYWALL_VIEWED: "Paywall Viewed",
  PAYWALL_CTA_TAPPED: "Paywall CTA Tapped",
  PAYWALL_AUTH_REQUIRED: "Paywall Auth Required",
  PAYWALL_PACKAGE_MISSING: "Paywall Package Missing",
  PURCHASE_INITIATED: "Purchase Initiated",
  PURCHASE_SUCCEEDED: "Purchase Succeeded",
  PURCHASE_FAILED: "Purchase Failed",
  PURCHASE_RESTORED: "Purchase Restored",
  SUBSCRIPTION_STARTED: "Subscription Started",
  TRIAL_ONBOARDING_SHOWN: "Trial Onboarding Shown",
  TRIAL_ONBOARDING_ACCEPTED: "Trial Onboarding Accepted",
  TRIAL_ONBOARDING_SKIPPED: "Trial Onboarding Skipped",

  // Integrations
  BROKERAGE_CONNECT_STARTED: "Brokerage Connect Started",
  BROKERAGE_CONNECT_CTA_TAPPED: "Brokerage Connect CTA Tapped",
  BROKERAGE_CONNECT_PICKER_VIEWED: "Brokerage Connect Picker Viewed",
  BROKERAGE_CONNECT_PICKER_FAILED: "Brokerage Connect Picker Failed",
  BROKERAGE_CONNECT_PICKER_CLOSED: "Brokerage Connect Picker Closed",
  BROKERAGE_CONNECT_SEARCH_PERFORMED: "Brokerage Connect Search Performed",
  BROKERAGE_CONNECT_BROKERAGE_SELECTED: "Brokerage Connect Brokerage Selected",
  BROKERAGE_CONNECT_LOGIN_LINK_REQUESTED:
    "Brokerage Connect Login Link Requested",
  BROKERAGE_CONNECT_LOGIN_LINK_SUCCEEDED:
    "Brokerage Connect Login Link Succeeded",
  BROKERAGE_CONNECT_LOGIN_LINK_FAILED: "Brokerage Connect Login Link Failed",
  BROKERAGE_CONNECT_PORTAL_OPENED: "Brokerage Connect Portal Opened",
  BROKERAGE_CONNECT_PORTAL_LOADED: "Brokerage Connect Portal Loaded",
  BROKERAGE_CONNECT_PORTAL_LOAD_FAILED: "Brokerage Connect Portal Load Failed",
  BROKERAGE_CONNECT_PORTAL_SUCCEEDED: "Brokerage Connect Portal Succeeded",
  BROKERAGE_CONNECT_PORTAL_FAILED: "Brokerage Connect Portal Failed",
  BROKERAGE_CONNECT_PORTAL_CANCELLED: "Brokerage Connect Portal Cancelled",
  BROKERAGE_CONNECT_SYNC_STARTED: "Brokerage Connect Sync Started",
  BROKERAGE_CONNECT_SYNC_SUCCEEDED: "Brokerage Connect Sync Succeeded",
  BROKERAGE_CONNECT_SYNC_FAILED: "Brokerage Connect Sync Failed",
  BROKERAGE_CONNECT_SYNC_VERIFIED: "Brokerage Connect Sync Verified",
  BROKERAGE_CONNECTED: "Brokerage Connected",

  // Account
  LOGOUT: "Logout",
  ACCOUNT_DELETED: "Account Deleted",

  // Feedback
  FEEDBACK_PROMPT_SHOWN: "Feedback Prompt Shown",
  STORE_REVIEW_NUDGE_REQUESTED: "Store Review Nudge Requested",
  STORE_REVIEW_NUDGE_UNAVAILABLE: "Store Review Nudge Unavailable",
  STORE_REVIEW_NUDGE_FAILED: "Store Review Nudge Failed",

  // Engagement Nudges
  ENGAGEMENT_NUDGE_VIEWED: "Engagement Nudge Viewed",
  ENGAGEMENT_NUDGE_PRIMARY_PRESSED: "Engagement Nudge Primary Pressed",
  ENGAGEMENT_NUDGE_PRIMARY_COMPLETED: "Engagement Nudge Primary Completed",
  ENGAGEMENT_NUDGE_PRIMARY_FAILED: "Engagement Nudge Primary Failed",
  ENGAGEMENT_NUDGE_DISMISSED: "Engagement Nudge Dismissed",

  // Push Notifications
  PUSH_PERMISSION_REQUESTED: "Push Permission Requested",
  PUSH_TOKEN_REGISTERED: "Push Token Registered",
  PUSH_TOKEN_SKIPPED: "Push Token Skipped",
  PUSH_TOKEN_FAILED: "Push Token Failed",
  ENGAGEMENT_PUSH_TAPPED: "Engagement Push Tapped",
} as const;
